// @flow
/**
 * A component that renders answer choices for a BotMessage.
 */
import React, { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { equals } from "ramda";

import {
  getLatestState,
  getMaxAnswersToInitiallyShow,
  getMessageAnswer,
  getMessagesHistory,
  getModuleById,
  getModuleByType,
  getModulesByName,
  getShowAllAnswerOptions,
} from "../../store";
import type { AnswerType } from "../../lib/types";
import { BackendModuleTypes, ModuleTypes } from "../../lib/types";
import { Answer } from "./Answer";
import { deepChange, goToModule, sendModuleAnswer } from "../../actions";
import { MessageContext } from "./BotMessageGenerator";
import DumbTextInput from "../InputField/DumbTextInput";
import { setMessageAnswer } from "./actions";

type Props = {
  answers: Array<AnswerType>,
};

export const Answers = (props: Props) => {
  const { answers } = props;

  const state = useStore().getState();
  const dispatch = useDispatch();

  const messageContext = useContext(MessageContext);

  const {
    messageNum,
    fromHistory,
    isLastMessage,
    isLocations,
    isErrorMessage,
    isWelcomeBackMessage,
    isFirstModule,
    isPricing,
    module,
    customMobilePreview,
  } = messageContext;

  const messageAnswer = useSelector((state) => getMessageAnswer({ state, messageNum }));
  const showAllAnswerOptions = useSelector(getShowAllAnswerOptions);
  const maxAnswersToInitiallyShow = useSelector(getMaxAnswersToInitiallyShow);
  const messagesHistory = useSelector(getMessagesHistory);

  const [initialShowAllAnswerOptions, setState] = useState(showAllAnswerOptions);

  // Le constructor. Runs only once. Hmm actually no - also runs on unmount.
  useEffect(() => {
    setState(showAllAnswerOptions);
  }, []); // eslint-disable-line

  /**
   * Show the rest of the module answers.
   */
  const showMoreAnswerOptions = () => {
    dispatch(deepChange({ showAllAnswerOptions: true }));
  };

  /**
   * On answer click handler.
   * @param i - answer iterable key. for tests compatibility. because I'm too lazy to fix them.
   * @param answerID
   */
  const onAnswerClick = ({ i, id: answerID }) => {
    if (fromHistory) {
      return;
    }
    const answer: AnswerType = typeof answerID !== "undefined" ? answers.find((a) => a.id === answerID) : answers[i];

    dispatch(setMessageAnswer({ messageNum, answer: answer.text }));
    // A hack for the Locations message to work - the answers get generated on the frontend
    // - it is not present in the modules data. The "Back" answer precisely speaking.
    // UPD: and this will fail miserably if we ever decide to show IA init input field here -
    // need to carefully rethink how the answers are addressed.
    if (isLocations || isErrorMessage) {
      dispatch(goToModule(answer.action));
      return;
    }
    dispatch(
      sendModuleAnswer(messagesHistory[messageNum].id, {
        id: answer.id,
        text: answer.text,
      })
    );
    if (module.key) {
      dispatch(deepChange({ path: ["userData", module.key], value: answer.text }));
    }
    dispatch(goToModule(answer.action));
  };

  let answersToRender = answers;

  const iaModule = getModuleByType(state, ModuleTypes.instantAnswers);
  let iaAnswer = null;

  if (iaModule) {
    const answersLeadingToIA = answersToRender.filter((a) => {
      const module = getModuleById(state, a.action);
      return module && module.type === ModuleTypes.instantAnswers;
    });
    iaAnswer = answersLeadingToIA.length && answersLeadingToIA[0];
  }

  if (iaAnswer) {
    // Exclude the answer that leads to IA and then render an input field instead.
    answersToRender = answersToRender.filter((a) => !equals(a, iaAnswer));
  }

  if (isWelcomeBackMessage && !showAllAnswerOptions) {
    // Filter answers shown in "welcome back" message.
    const moduleTypesToWelcomeBack = [BackendModuleTypes.OPEN_SCHEDULE_TOUR, BackendModuleTypes.ANSWER_FLOW];
    const state = dispatch(getLatestState);
    const moduleIds = Object.entries(getModulesByName(state))
      .filter(([key]) => moduleTypesToWelcomeBack.includes(key))
      .map(([key, value]) => value);
    answersToRender = answersToRender.filter((answer) => moduleIds.includes(answer.action));
  } else if (isFirstModule && !showAllAnswerOptions) {
    // Limit the number of answers for the first module initially.
    answersToRender = answersToRender.slice(0, maxAnswersToInitiallyShow);

    // Render one more less when there is IA input field.
    // Skip reduction if custom mobile preview flag is set (disableMobile)
    if (iaAnswer && !customMobilePreview) {
      answersToRender = answersToRender.slice(0, answersToRender.length - 1);
    }
  } else if (isPricing) {
    // Render max 3 answers for the pricing module.
    answersToRender = answersToRender.slice(0, 3);
  }

  let result = answersToRender.map<AnswerType>((answer, i) => {
    const { text, id } = answer;
    let className = text === messageAnswer ? "active" : "";
    if (!initialShowAllAnswerOptions && showAllAnswerOptions && i >= maxAnswersToInitiallyShow) {
      className += " delayed";
    }
    if (module.isVideo() || module.isDigitalTour()) {
      className += " multi bright";
    }

    // Want to make PricingModule buttons a little bit smaller.
    if (isPricing) {
      className += " pricing";
    }
    return (
      <Answer key={i} onClick={() => onAnswerClick({ id, i })} className={className}>
        {text}
      </Answer>
    );
  });

  // Add an extra option to toggle the rest of the answers (if any) visible.

  if (isFirstModule && !showAllAnswerOptions) {
    const ShowMoreOptionsAnswer = (
      <Answer key={-1} onClick={showMoreAnswerOptions}>
        Show More
      </Answer>
    );
    result = [...result, ShowMoreOptionsAnswer];
  }

  // Render the input field answer leading to Instant Answers workflow.
  // Skip rendering if custom mobile preview flag is set (disableMobile).
  if (iaAnswer && isLastMessage && !customMobilePreview) {
    result = [...result, <IAFakeInput iaAnswer={iaAnswer} key="ia-activate" />];
  }

  return result;
};

type IAFakeInputProps = {
  iaAnswer: any,
};

const IAFakeInput = ({ iaAnswer }: IAFakeInputProps) => {
  const dispatch = useDispatch();

  return <DumbTextInput placeholder={iaAnswer.text} onClick={() => dispatch(goToModule(iaAnswer.action))} />;
};
